import { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import io from 'socket.io-client';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, TimeScale, Title, Tooltip, Legend } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { Card, Button, Alert, Modal, Form } from 'react-bootstrap';
import StatusBadge from './ui/StatusBadge';
import Sparkline from './ui/Sparkline';
import EmptyState from './ui/EmptyState';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, TimeScale, Title, Tooltip, Legend);

const socket = io(process.env.REACT_APP_API_URL || '');
const SPARKLINE_LEN = 20;

const isConnected = (timestamp) => {
  if (!timestamp) return false;
  return Date.now() - new Date(timestamp).getTime() < 30 * 1000;
};

const getDisplayEntries = (device) =>
  Object.entries(device.data || {})
    .filter(([key]) => !key.startsWith('$') && !key.startsWith('__')
      && !['userId', 'deviceId', 'deviceSecret'].includes(key))
    .map(([key, value]) => {
      const isActionPin = device.actionPins?.some((pin) => pin.actionPinID === key);
      let displayName = key.replace(/_([a-z])/g, (_, l) => ' ' + l.toUpperCase()).replace(/^([a-z])/, (l) => l.toUpperCase());

      if (!isActionPin) {
        let sensor = device.sensors?.find((s) => s.sensorID === key);
        if (!sensor && (key === 'undefined' || key === 'null')) {
          sensor = device.sensors?.find((s) => !s.sensorID);
        }
        if (sensor) displayName = sensor.name || sensor.sensorType;
      } else {
        const pin = device.actionPins?.find((p) => p.actionPinID === key);
        if (pin) displayName = pin.name || pin.type;
      }

      const displayValue = typeof value === 'object'
        ? '[Object]'
        : isActionPin
          ? (Number(value) === 1 ? 'On' : Number(value) === 0 ? 'Off' : value)
          : value;

      return { key, displayName, displayValue, numeric: !isActionPin && typeof value === 'number' ? value : null };
    });

const DeviceList = ({ onDevicesChange, onRegisterDevice, hasReachedLimit = false }) => {
  const [devices, setDevices] = useState([]);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteInput, setDeleteInput] = useState('');
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const sparklineHistory = useRef({});
  const { token } = useContext(AuthContext);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (onDevicesChange) onDevicesChange(devices);
  }, [devices, onDevicesChange]);

  const updateSparkline = (deviceId, data) => {
    const history = sparklineHistory.current[deviceId] || {};
    Object.entries(data || {}).forEach(([key, value]) => {
      if (typeof value === 'number') {
        if (!history[key]) history[key] = [];
        history[key] = [...history[key], value].slice(-SPARKLINE_LEN);
      }
    });
    sparklineHistory.current[deviceId] = history;
  };

  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL || ''}/api/devices`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const fetched = response.data.devices;
        fetched.forEach((d) => updateSparkline(d.deviceId, d.data));
        setDevices(fetched);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to fetch devices');
      }
    };
    fetchDevices();

    socket.emit('authenticate', token);

    socket.on('newDeviceData', (updatedDevice) => {
      updateSparkline(updatedDevice.deviceId, updatedDevice.data);
      setDevices((prev) =>
        prev.map((device) =>
          device._id === updatedDevice._id ? { ...device, ...updatedDevice } : device
        )
      );
    });

    return () => socket.off('newDeviceData');
  }, [token]);

  const navigateToDevice = (device) => {
    if (!device.deviceId) return;
    navigate(`/app/device/${device.deviceId}`, {
      state: {
        sensors: device.sensors || [],
        actionPins: device.actionPins || [],
        automations: device.automations || [],
        deviceName: device.deviceName,
      },
    });
  };

  if (error) return <Alert variant="danger">{error}</Alert>;

  if (devices.length === 0) {
    return (
      <EmptyState
        title="No devices yet"
        description="Register your first ESP32 device to start monitoring sensors and controlling your garden."
        actionLabel="Register Device"
        onAction={onRegisterDevice}
        disabled={hasReachedLimit}
      />
    );
  }

  return (
    <>
      <div className="row">
        {devices.map((device) => {
          const entries = getDisplayEntries(device);
          const connected = isConnected(device.timestamp);
          const sparkData = entries.find((e) => e.numeric != null);
          const sparkKey = sparkData?.key;
          const sparkValues = sparkKey
            ? sparklineHistory.current[device.deviceId]?.[sparkKey] || []
            : [];

          return (
            <div key={device._id} className="col-md-4 mb-4">
              <Card className="h-100 device-card app-surface-card">
                <Card.Body>
                  <div className="device-card-header mb-2">
                    <Card.Title
                      className="main mb-0"
                      style={{ cursor: 'pointer', fontFamily: 'var(--font-display)', fontSize: '1.1rem' }}
                      onClick={() => navigateToDevice(device)}
                    >
                      {device.deviceName}
                    </Card.Title>
                    <StatusBadge connected={connected} timestamp={device.timestamp} />
                  </div>

                  <div className="device-card-metrics">
                    {entries.slice(0, 3).map(({ key, displayName, displayValue }) => (
                      <div key={key}>{displayName}: {displayValue}</div>
                    ))}
                    {entries.length > 3 && (
                      <div className="text-muted">+{entries.length - 3} more</div>
                    )}
                  </div>

                  {sparkValues.length > 1 && (
                    <Sparkline data={sparkValues} color="var(--main-green)" width={220} height={36} />
                  )}

                  <div className="device-card-actions mt-3">
                    <Button variant="secondary" size="sm" onClick={() => navigateToDevice(device)}>
                      Monitor
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => navigate(`/app/devices/edit/${device.deviceId}`, {
                        state: {
                          board: device.board,
                          sensors: device.sensors || [],
                          actionPins: device.actionPins || [],
                          automations: device.automations || [],
                          deviceName: device.deviceName,
                        },
                      })}
                    >
                      Configure
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => {
                        setDeviceToDelete(device);
                        setShowDeleteModal(true);
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </div>
          );
        })}
      </div>

      <Modal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setDeleteInput('');
          setDeviceToDelete(null);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Device Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Delete <strong>{deviceToDelete?.deviceName}</strong>? This cannot be undone.</p>
          <Form.Group>
            <Form.Label>Type <strong>DELETE</strong> to confirm:</Form.Label>
            <Form.Control
              type="text"
              value={deleteInput}
              onChange={(e) => setDeleteInput(e.target.value)}
              placeholder="Type DELETE to confirm"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button className="button secondary" onClick={() => {
            setShowDeleteModal(false);
            setDeleteInput('');
            setDeviceToDelete(null);
          }}>
            Cancel
          </Button>
          <Button
            className="button danger"
            disabled={deleteInput !== 'DELETE'}
            onClick={async () => {
              if (!deviceToDelete) return;
              try {
                await axios.delete(`${process.env.REACT_APP_API_URL || ''}/api/devices/${deviceToDelete.deviceId}`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                const response = await axios.get(`${process.env.REACT_APP_API_URL || ''}/api/devices`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                setDevices(response.data.devices);
                setShowDeleteModal(false);
                setDeleteInput('');
                setDeviceToDelete(null);
              } catch (err) {
                toast.error('Delete failed: ' + (err.response?.data?.error || 'Server error'));
                setShowDeleteModal(false);
                setDeleteInput('');
                setDeviceToDelete(null);
              }
            }}
          >
            Delete Device
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DeviceList;